import { Component, OnInit, Output, EventEmitter, ViewChild } from '@angular/core';
import { Property } from '../models/property';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { PropertySearchRequest } from '../../shared/models/property-search-request';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { PropertyLookupSummary } from '../../shared/models/property-lookup-summary';
import { tap, catchError } from 'rxjs/operators';
import { PioneerUtils } from '../utils/pioneer-utils';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { PropertyLookupDetail } from '../../shared/models/property-lookup-detail';
import { Page } from '../../shared/models/page';
import { PropertyLookupConverter } from '../utils/property-lookup-converter';
import { DropdownItem } from '../models/dropdown-item';
import { CommonDataStore } from '../service/common-data/common-data.store';
import { ChainBrandStore } from '../service/chainbrand/chain-brand.store';
import { DropdownSearchComponent } from '../../shared/dropdown-search/dropdown-search.component';

@Component({
  selector: 'pioneer-property-search',
  templateUrl: './property-search.component.html',
  styleUrls: [ './property-search.component.css' ]
})
export class PropertySearchComponent implements OnInit {

  @Output()
  canceled = new EventEmitter();

  @Output()
  selected = new EventEmitter<Property>();

  @Output()
  goToAdd = new EventEmitter();

  @ViewChild('chainDropdown')
  chainDropdown: DropdownSearchComponent;

  @ViewChild('brandDropdown')
  brandDropdown: DropdownSearchComponent;

  @ViewChild('countryDropdown')
  countryDropdown: DropdownSearchComponent;

  @ViewChild('stateDropdown')
  stateDropdown: DropdownSearchComponent;

  propertySearchForm: FormGroup;

  searchResults: Observable<List<PropertyLookupSummary>>;

  totalItems: Observable<number>;

  size: Observable<number>;

  chains$: Observable<DropdownItem[]>;
  brands$: Observable<DropdownItem[]>;
  states$: Observable<DropdownItem[]>;

  private readonly propertySearchFormControlsConfig = {
    chain: [ '', [] ],
    brand: [ '', [] ],
    property: [ '', [] ],
    country: [ '', [] ],
    state: [ '', [] ],
    city: [ '', [] ],
    onlySubscribers: [ false, [] ]
  };

  private readonly propertySearchFormValidators = {
    validator: Validators.compose([
      PioneerValidators.oneIsRequiredValidator('chain', 'brand', 'property', 'country', 'state', 'city')
    ])
  };

  list = [];

  constructor(private formBuilder: FormBuilder,
              private propertyLookupStore: PropertyLookupStore,
              public commonDataStore: CommonDataStore,
              private chainBrandStore: ChainBrandStore) { }

  ngOnInit() {
    this.reset();
  }

  cancel() {
    this.canceled.emit();
  }

  selectProperty(selectedPropertyLookupSummary: PropertyLookupSummary): void {
    const obs$ = this.propertyLookupStore.getDetail(selectedPropertyLookupSummary)
      .pipe(
        tap((selectedPropertyLookupDetail: PropertyLookupDetail) => {
          this.selected.emit(PropertyLookupConverter.convertPropertyLookupDetailToProperty(selectedPropertyLookupDetail));
          this.reset();
        }),
        catchError(() => {
          return Observable.of(null);
        })
      );
    obs$.take(1).subscribe();
  }

  goToAddProperty(): void {
    this.goToAdd.emit();
  }

  search() {
    const propertySearchRequest = this.extractPropertySearchRequest();
    const obs$ = this.propertyLookupStore.advancedSearch(propertySearchRequest)
      .take(1)
      .pipe(
        catchError(() => {
          return Observable.of(null);
        })
      );
    this.searchResults = obs$.map((page: Page<PropertyLookupSummary>) => {
      this.totalItems = this.propertyLookupStore.advancedSearchTotalElements;
      this.size = this.propertyLookupStore.advancedSearchSize;
      return page.content;
    });
  }

  chainSelected(chain: DropdownItem) {
    if (this.brandDropdown) {
      this.brandDropdown.reset();
    }
    if (chain) {
      this.brands$ = this.chainBrandStore.loadBrandsFromChain(chain.key);
    } else {
      this.brands$ = Observable.of([]);
    }
  }

  countrySelected(country: DropdownItem) {
    if (this.stateDropdown) {
      this.stateDropdown.reset();
    }
    if (country) {
      this.states$ = this.commonDataStore.loadStatesFromCountry(country.key);
    } else {
      this.states$ = Observable.of([]);
    }
  }

  reset(): void {
    this.chains$ = this.chainBrandStore.chains;
    this.brands$ = Observable.of([]);
    this.states$ = Observable.of([]);
    this.chainDropdown.reset();
    this.brandDropdown.reset();
    this.countryDropdown.reset();
    if (this.stateDropdown) {
      this.stateDropdown.reset();
    }
    this.searchResults = Observable.of(List([]));
    this.createForm();
    this.propertySearchForm.enable();
  }

  private createForm() {
    this.propertySearchForm = this.formBuilder.group(this.propertySearchFormControlsConfig, this.propertySearchFormValidators);
  }

  private extractPropertySearchRequest() {
    return new PropertySearchRequest({
      chain: PioneerUtils.trimToNull(this.propertySearchForm.get('chain').value),
      brand: PioneerUtils.trimToNull(this.propertySearchForm.get('brand').value),
      property: PioneerUtils.trimToNull(this.propertySearchForm.get('property').value),
      city: PioneerUtils.trimToNull(this.propertySearchForm.get('city').value),
      stateCode: PioneerUtils.trimToNull(this.propertySearchForm.get('state').value),
      countryCode: PioneerUtils.trimToNull(this.propertySearchForm.get('country').value),
      onlySubscribers: this.propertySearchForm.get('onlySubscribers').value
    });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/property-search/property-search.component.ts